import { useState } from "react";
import { toast } from "sonner";
import { Mail, Send, Loader2 } from "lucide-react";
import { Reveal, Label } from "./Reveal";
import { submitContact } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Thank you! Your message has been sent. We'll reply shortly.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact-section"
      data-testid="contact-section"
      className="py-20 lg:py-32 px-4 sm:px-8 lg:px-12 bg-[#2F3B2E] text-white"
    >
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <Reveal>
          <span className="font-mono-accent text-xs uppercase tracking-[0.25em] text-[#C9B48A]">
            Get In Touch
          </span>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight">
            Have a question before booking?
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-white/75 max-w-md">
            Send us a note and the Mended Alchemy team will get back to you
            within one business day. No pressure, no obligation — just honest
            guidance.
          </p>
          <div className="mt-8 flex items-center gap-3 text-white/80">
            <Mail size={18} className="text-[#C9B48A]" />
            <span className="text-sm">A member of our team reads every message personally.</span>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-3xl bg-white p-7 lg:p-9 text-[#3B352D]"
          >
            <div className="space-y-4">
              <div>
                <Label>Your Name</Label>
                <input
                  data-testid="contact-name"
                  value={form.name}
                  onChange={update("name")}
                  className="mt-2 w-full rounded-xl border border-[#2F3B2E]/20 bg-[#FAF7F0] px-4 py-3 text-sm outline-none focus:border-[#2F3B2E] transition-colors"
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <Label>Email Address</Label>
                <input
                  data-testid="contact-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  className="mt-2 w-full rounded-xl border border-[#2F3B2E]/20 bg-[#FAF7F0] px-4 py-3 text-sm outline-none focus:border-[#2F3B2E] transition-colors"
                  placeholder="jane@email.com"
                />
              </div>
              <div>
                <Label>Message</Label>
                <textarea
                  data-testid="contact-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={4}
                  className="mt-2 w-full rounded-xl border border-[#2F3B2E]/20 bg-[#FAF7F0] px-4 py-3 text-sm outline-none focus:border-[#2F3B2E] transition-colors resize-none"
                  placeholder="Tell us what's on your mind..."
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2F3B2E] py-3.5 text-sm font-medium text-white hover:bg-[#232B22] hover:text-white transition-colors duration-200 disabled:opacity-70"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {loading ? "Sending..." : "Send Message"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
